/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 11-05-2026
 *
 * Angular-implementatie van de AngularJS beginnersuitleg-directive.
 * Toont een uitlegballon bij hover over het host-element als de beginnersmodus actief is.
 * De ballon wordt op document.body gerenderd met viewport-based positionering,
 * zodat hij altijd zichtbaar is ongeacht de positie van het host-element.
 * De stijlen staan in style/beginnersuitleg.scss (globaal geladen, want de ballon hangt aan
 * document.body en valt dus buiten elke component-encapsulatie).
 */
import { Directive, HostListener, Input, OnDestroy, ElementRef, effect, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { KeyedTimeout } from 'sihw-ngx';
import { WeergaveInstellingenService } from '../../services/weergave-instellingen.service';

const OPEN_DEBOUNCE_MS = 50;
const CLOSE_DEBOUNCE_MS = 250;
const PIJL_HALF_BREEDTE = 10;
const MIN_PIJL_MARGIN = 4;
const PIJL_BIAS_PX = 2;

/**
 * Globale registry van alle actieve directive-instanties.
 * Hiermee kunnen we bij het openen van een nieuwe ballon direct alle andere sluiten.
 */
const alleInstanties = new Set<BeginneruitlegDirective>();

/**
 * Actieve programmatische ballon (buiten een directive-instantie om).
 * Wordt gebruikt door toonOpElement voor ballonnen op willekeurige DOM-elementen.
 */
let programmatischeBallon: HTMLElement | null = null;

/**
 * De auto-sluit-timer van de programmatische ballon
 */
const programmatischeTimer = new KeyedTimeout();

/**
 * Statische referentie naar de vertaalservice, ingesteld door de eerste directive-instantie.
 * Hierdoor kan toonOpElement vertalingen ophalen zonder een instantie nodig te hebben.
 */
let statischeVertaalService: TranslateService | null = null;

@Directive({
    selector: '[dlBeginnersuitleg]',
    standalone: true,
})
export class BeginneruitlegDirective implements OnDestroy {

    /**
     * Sluit elke openstaande uitlegballon: die van de directive-instanties en een eventuele
     * programmatisch getoonde ballon. Voor situaties waarin iets anders het scherm opeist, zoals een
     * dropdown die over de ballon heen zou vallen.
     */
    static sluitAlle(): void {
        for (const instantie of alleInstanties) {
            instantie.sluitDirect();
        }
        if (programmatischeBallon) {
            programmatischeTimer.clear('ballon');
            programmatischeBallon.remove();
            programmatischeBallon = null;
        }
    }

    /**
     * Toont een uitlegballon bij een willekeurig DOM-element op basis van element-id en vertaalsleutel.
     * Sluit alle bestaande directive-ballonnen en een eventuele vorige programmatische ballon.
     * De ballon verdwijnt automatisch na 4 seconden.
     */
    static toonOpElement(element: HTMLElement, translatekey: string): void {
        const tekst = statischeVertaalService?.instant(translatekey) ?? translatekey;
        BeginneruitlegDirective.sluitAlle();


        const container = document.createElement('div');
        container.className = 'dl-beginnersuitleg__container';

        const ballon = document.createElement('div');
        ballon.className = 'dl-beginnersuitleg__ballon dl-beginnersuitleg__ballon--melding';
        ballon.setAttribute('role', 'tooltip');

        const tekstDiv = document.createElement('div');
        tekstDiv.className = 'dl-beginnersuitleg__tekst';
        tekstDiv.textContent = tekst;
        ballon.appendChild(tekstDiv);
        container.appendChild(ballon);
        document.body.appendChild(container);

        // Positioneer onder (of boven) het element
        const rDoel = element.getBoundingClientRect();
        const ruimteOnder = window.innerHeight - rDoel.bottom;
        const bovenElement = ruimteOnder < 150;
        const knopMidden = (rDoel.left + rDoel.right) / 2;
        const ballonBreedte = ballon.getBoundingClientRect().width || 200;

        let left = rDoel.left;
        let rechtsUitgelijnd = false;
        if ((left + ballonBreedte) > window.innerWidth) {
            rechtsUitgelijnd = true;
            left = rDoel.right - ballonBreedte;
        }
        if (left < 0) left = 0;

        let pijlLeft: number | null = null;
        let pijlRight: number | null = null;
        if (!rechtsUitgelijnd) {
            pijlLeft = Math.round(Math.max(MIN_PIJL_MARGIN, Math.min((knopMidden - left) - PIJL_HALF_BREEDTE + PIJL_BIAS_PX, ballonBreedte - MIN_PIJL_MARGIN - 2 * PIJL_HALF_BREEDTE)));
        } else {
            const pijlRightRaw = (left + ballonBreedte - knopMidden) - PIJL_HALF_BREEDTE + PIJL_BIAS_PX;
            pijlRight = Math.round(Math.max(MIN_PIJL_MARGIN, Math.min(pijlRightRaw, ballonBreedte - MIN_PIJL_MARGIN - 2 * PIJL_HALF_BREEDTE)));
        }

        if (pijlLeft !== null) {
            ballon.style.setProperty('--pijl-left', pijlLeft + 'px');
        } else if (pijlRight !== null) {
            ballon.style.setProperty('--pijl-right', pijlRight + 'px');
        }
        ballon.classList.toggle('dl-beginnersuitleg__ballon--rechts-uitgelijnd', rechtsUitgelijnd);
        ballon.classList.toggle('dl-beginnersuitleg__ballon--beneden', bovenElement);

        container.style.left = Math.round(left) + 'px';
        container.style.right = 'auto';
        if (bovenElement) {
            container.style.bottom = Math.round(window.innerHeight - rDoel.top) + 'px';
            container.style.top = 'auto';
        } else {
            container.style.top = Math.round(rDoel.bottom) + 'px';
            container.style.bottom = 'auto';
        }

        programmatischeTimer.set('ballon', () => {
            if (container.parentNode) container.parentNode.removeChild(container);
            programmatischeBallon = null;
        }, 4000);
        programmatischeBallon = container;
    }

    private readonly elementRef = inject(ElementRef<HTMLElement>);
    private readonly translateService = inject(TranslateService);

    /**
     * De weergave-instellingen: bepalen of de beginnersmodus (en dus de ballon) actief is.
     */
    private readonly weergaveInstellingen = inject(WeergaveInstellingenService);

    /**
     * De vertaalsleutel voor de uitlegballon (zonder prefix).
     * De volledige sleutel wordt: BEGINNERSUITLEG.<waarde>
     */
    @Input() dlBeginnersuitleg = '';

    private ballonContainer: HTMLElement | null = null;

    /**
     * De open- en sluit-debounce-timers ('open'/'sluit')
     */
    private readonly timers = new KeyedTimeout();

    constructor() {
        alleInstanties.add(this);
        statischeVertaalService ??= this.translateService;
        // Bij het uitzetten van de beginnersmodus wordt een eventueel open ballon direct gesloten
        effect(() => {
            if (!this.weergaveInstellingen.beginnersmodusAan()) {
                this.sluitDirect();
            }
        });
    }

    ngOnDestroy(): void {
        alleInstanties.delete(this);
        this.timers.clearAll();
        this.verwijderBallon();
    }

    @HostListener('pointerenter')
    bijMuisIn(): void {
        this.timers.clear('sluit');
        this.timers.set('open', () => this.openBallon(), OPEN_DEBOUNCE_MS);
    }

    @HostListener('pointerleave')
    bijMuisUit(): void {
        this.timers.clear('open');
        this.timers.set('sluit', () => this.sluitBallon(), CLOSE_DEBOUNCE_MS);
    }

    /**
     * Opent de uitlegballon op document.body, gepositioneerd onder (of boven) het host-element.
     * Sluit eerst direct alle andere open ballonnen (zonder debounce).
     */
    private openBallon(): void {
        if (!this.weergaveInstellingen.beginnersmodusAan()) return;

        // Sluit alle andere ballonnen direct, zonder debounce
        for (const instantie of alleInstanties) {
            if (instantie !== this) {
                instantie.sluitDirect();
            }
        }

        const vertaalKey = 'BEGINNERSUITLEG.' + this.dlBeginnersuitleg;
        const tekst = this.translateService.instant(vertaalKey);

        if (!this.ballonContainer) {
            this.ballonContainer = document.createElement('div');
            this.ballonContainer.className = 'dl-beginnersuitleg__container';

            const ballon = document.createElement('div');
            ballon.className = 'dl-beginnersuitleg__ballon';
            ballon.setAttribute('role', 'tooltip');

            const tekstDiv = document.createElement('div');
            tekstDiv.className = 'dl-beginnersuitleg__tekst';
            ballon.appendChild(tekstDiv);
            this.ballonContainer.appendChild(ballon);

            // Ballon-hover: annuleer sluiten bij hover over de ballon zelf
            ballon.addEventListener('pointerenter', () => {
                this.timers.clear('sluit');
            });
            ballon.addEventListener('pointerleave', () => this.bijMuisUit());
        }

        // Tekst bijwerken
        const tekstDiv = this.ballonContainer.querySelector('.dl-beginnersuitleg__tekst') as HTMLElement;
        if (tekstDiv) tekstDiv.textContent = tekst;

        document.body.appendChild(this.ballonContainer);
        this.positioneer();
    }

    /**
     * Sluit de ballon na de standaard debounce-tijd.
     */
    private sluitBallon(): void {
        this.verwijderBallon();
    }

    /**
     * Sluit de ballon direct, zonder debounce. Annuleert ook lopende timers.
     */
    sluitDirect(): void {
        this.timers.clearAll();
        this.verwijderBallon();
    }

    /**
     * Verwijdert het ballon-element uit de DOM als het aanwezig is.
     */
    private verwijderBallon(): void {
        if (this.ballonContainer && this.ballonContainer.parentNode) {
            this.ballonContainer.parentNode.removeChild(this.ballonContainer);
        }
    }

    /**
     * Positioneert de ballon t.o.v. het host-element, viewport-based.
     * Pijlpunt wijst naar het midden van de knop.
     */
    private positioneer(): void {
        if (!this.ballonContainer) return;
        const host = this.elementRef.nativeElement;
        const rDoel = host.getBoundingClientRect();
        const ballon = this.ballonContainer.querySelector('.dl-beginnersuitleg__ballon') as HTMLElement;

        const ruimteOnder = window.innerHeight - rDoel.bottom;
        const bovenElement = ruimteOnder < 150;

        const knopMidden = (rDoel.left + rDoel.right) / 2;
        const ballonBreedte = ballon ? ballon.getBoundingClientRect().width || 200 : 200;

        let left = rDoel.left;
        let rechtsUitgelijnd = false;

        if ((left + ballonBreedte) > window.innerWidth) {
            rechtsUitgelijnd = true;
            left = rDoel.right - ballonBreedte;
        }
        if (left < 0) left = 0;

        // Pijlpositie berekenen
        let pijlLeft: number | null = null;
        let pijlRight: number | null = null;

        if (!rechtsUitgelijnd) {
            const offset = knopMidden - left;
            pijlLeft = Math.round(Math.max(MIN_PIJL_MARGIN, Math.min(offset - PIJL_HALF_BREEDTE + PIJL_BIAS_PX, ballonBreedte - MIN_PIJL_MARGIN - 2 * PIJL_HALF_BREEDTE)));
        } else {
            const ballonRechts = left + ballonBreedte;
            const offset = ballonRechts - knopMidden;
            const pijlRightRaw = offset - PIJL_HALF_BREEDTE + PIJL_BIAS_PX;
            pijlRight = Math.round(Math.max(MIN_PIJL_MARGIN, Math.min(pijlRightRaw, ballonBreedte - MIN_PIJL_MARGIN - 2 * PIJL_HALF_BREEDTE)));
            if (pijlRightRaw < MIN_PIJL_MARGIN) {
                left += MIN_PIJL_MARGIN - pijlRightRaw;
            }
        }

        // CSS-variabelen voor pijlpositie
        if (ballon) {
            if (pijlLeft !== null) {
                ballon.style.setProperty('--pijl-left', pijlLeft + 'px');
                ballon.style.removeProperty('--pijl-right');
            } else if (pijlRight !== null) {
                ballon.style.setProperty('--pijl-right', pijlRight + 'px');
                ballon.style.removeProperty('--pijl-left');
            }
            ballon.classList.toggle('dl-beginnersuitleg__ballon--rechts-uitgelijnd', rechtsUitgelijnd);
            ballon.classList.toggle('dl-beginnersuitleg__ballon--beneden', bovenElement);
        }

        // Container positioneren
        this.ballonContainer.style.left = Math.round(left) + 'px';
        this.ballonContainer.style.right = 'auto';

        if (bovenElement) {
            this.ballonContainer.style.top = 'auto';
            this.ballonContainer.style.bottom = (window.innerHeight - rDoel.top + 15) + 'px';
        } else {
            this.ballonContainer.style.top = (rDoel.bottom + 5) + 'px';
            this.ballonContainer.style.bottom = 'auto';
        }
    }
}
